import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Loader2, CheckCircle2 } from "lucide-react";

const field = "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 outline-none focus:border-primary/50";
const PROFILE_TYPES = ["Entrepreneur", "Partner", "Ambassador", "Community Member", "Other"];
const PROFILE_FR = { Entrepreneur: "Entrepreneur", Partner: "Partenaire", Ambassador: "Ambassadeur", "Community Member": "Membre de la communauté", Other: "Autre" };

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", phone: "", company: "", profile_type: "Community Member" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await register(f);
    setLoading(false);
    if (res.ok) setDone(res.message);
    else setError(res.error);
  };

  if (done) {
    return (
      <div className="relative flex min-h-screen items-center justify-center px-4">
        <div className="absolute inset-0 bg-background" />
        <div className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-[hsl(var(--surface))]/90 p-8 text-center backdrop-blur-xl" data-testid="register-pending">
          <CheckCircle2 className="mx-auto h-14 w-14 text-primary" />
          <h1 className="mt-4 font-heading text-2xl font-bold">Inscription reçue</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{done}</p>
          <Link to="/" className="mt-6 inline-block rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground">{t("common.backHome")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4 py-10">
      <img src="https://images.unsplash.com/photo-1783979384797-7a5d2ad23fc8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-background/80" />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-[hsl(var(--surface))]/90 p-8 backdrop-blur-xl">
        <Link to="/" className="font-heading text-2xl font-black tracking-tight">Vytali<span className="text-primary">Flix</span></Link>
        <h1 className="mt-6 font-heading text-2xl font-bold">{t("auth.registerTitle")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Accès sur approbation. Le contenu gratuit reste visible sans compte.</p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Prénom" value={f.first_name} onChange={(e) => upd("first_name", e.target.value)} data-testid="register-firstname" className={field} />
            <input required placeholder="Nom" value={f.last_name} onChange={(e) => upd("last_name", e.target.value)} data-testid="register-lastname" className={field} />
          </div>
          <input type="email" required placeholder="E-mail" value={f.email} onChange={(e) => upd("email", e.target.value)} data-testid="register-email" className={field} />
          <input type="password" required placeholder="Mot de passe" value={f.password} onChange={(e) => upd("password", e.target.value)} data-testid="register-password" className={field} />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Téléphone (optionnel)" value={f.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="register-phone" className={field} />
            <input placeholder="Société (optionnel)" value={f.company} onChange={(e) => upd("company", e.target.value)} data-testid="register-company" className={field} />
          </div>
          <select value={f.profile_type} onChange={(e) => upd("profile_type", e.target.value)} data-testid="register-profile-type" className={field}>
            {PROFILE_TYPES.map((p) => <option key={p} value={p}>{PROFILE_FR[p]}</option>)}
          </select>
          {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="register-submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 font-semibold text-primary-foreground transition-colors hover:bg-[hsl(var(--primary))]/90 disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.registerCta")}
          </button>
        </form>

        <p className="mt-6 text-sm text-muted-foreground">{t("auth.hasAccount")} <Link to="/login" className="text-primary hover:underline">{t("nav.login")}</Link></p>
      </div>
    </div>
  );
}
